import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Plus, Image, Calendar, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { LOCAL_ALBUMS } from '@/data/albums';

export default function AdminGallery() {
    const [albums, setAlbums] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAlbums();
    }, []);

    const fetchAlbums = async () => {
        try {
            const { data, error } = await supabase
                .from('albums')
                .select('*, photos(count)')
                .order('date', { ascending: false });

            if (error) throw error;
            
            const dbAlbums = (data || []).map(album => ({
                ...album,
                source: 'database',
                photoCount: album.photos[0]?.count || 0
            }));

            const localAlbums = LOCAL_ALBUMS.map(album => ({
                ...album,
                source: 'local',
                photoCount: album.photos.length,
                cover_image_url: album.coverImage // Map to match DB field name
            }));

            const combined = [...localAlbums, ...dbAlbums].sort((a, b) => 
                new Date(b.date) - new Date(a.date)
            );

            setAlbums(combined);
        } catch (error) {
            console.error('Error fetching albums:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet album ? Toutes les photos associées seront également supprimées.')) return;

        try {
            const { error } = await supabase
                .from('albums')
                .delete()
                .eq('id', id);

            if (error) throw error;
            fetchAlbums();
        } catch (error) {
            console.error('Error deleting album:', error);
            alert('Erreur lors de la suppression de l\'album');
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-serif font-bold text-gray-900">Galerie Photos</h1>
                <Link
                    to="/admin/gallery/new"
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                    <Plus size={20} />
                    Nouvel Album
                </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {albums.map((album) => (
                    <div key={album.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden group">
                        <div className="relative aspect-video bg-gray-100">
                            {album.cover_image_url ? (
                                <img
                                    src={album.cover_image_url}
                                    alt={album.title}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                    <Image size={48} />
                                </div>
                            )}
                            
                            {/* Source Badge */}
                            <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-sm z-10 ${
                                album.source === 'local' 
                                    ? 'bg-amber-100 text-amber-700 border border-amber-200' 
                                    : 'bg-blue-100 text-blue-700 border border-blue-200'
                            }`}>
                                {album.source === 'local' ? 'Public (Local)' : 'Base de données'}
                            </div>

                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                                {album.source === 'database' ? (
                                    <>
                                        <Link
                                            to={`/admin/gallery/${album.id}`}
                                            className="px-4 py-2 bg-white text-primary-900 rounded-lg font-medium hover:bg-gray-100"
                                        >
                                            Gérer
                                        </Link>
                                        <button
                                            onClick={() => handleDelete(album.id)}
                                            className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                                        >
                                            <Trash2 size={20} />
                                        </button>
                                    </>
                                ) : (
                                    <div className="text-white text-sm text-center px-4">
                                        Géré via le code source
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="p-4">
                            <h3 className="font-bold text-gray-900 truncate">{album.title}</h3>
                            <div className="flex items-center justify-between mt-2 text-sm text-gray-500">
                                <div className="flex items-center gap-1">
                                    <Calendar size={14} />
                                    <span>{format(new Date(album.date), 'd MMMM yyyy', { locale: fr })}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Image size={14} />
                                    <span>{album.photoCount} photos</span>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                {albums.length === 0 && (
                    <div className="col-span-full py-12 text-center text-gray-500 bg-gray-50 rounded-xl border-dashed border-2 border-gray-200">
                        <Image size={48} className="mx-auto mb-4 opacity-50" />
                        <p>Aucun album pour le moment.</p>
                        <p className="text-sm">Créez votre premier album pour commencer la galerie.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
